import { Language, Region } from '../types/Internationalization';

const BASE_WHOOP_URL = 'https://whoop.com';
const DEFAULT_LANGUAGE = Language.EN;
const DEFAULT_REGION = Region.US;

const isDefaultLanguage = (language: Language): boolean => {
  return language === DEFAULT_LANGUAGE;
};

const getLanguageModifier = (language: Language) => {
  const onEnglishSite = isDefaultLanguage(language);
  if (onEnglishSite) return '';
  return `/${language}`;
};

const getRegionModifier = (region: Region, language: Language) => {
  if (language === Language.DE) return '/germany';
  if (['EU', 'UK'].includes(region)) return '/eu-uk';
  return '';
};

const getShopRegion = (region: Region) => {
  return region === Region.NZ
    ? Region.AU
    : region;
}

const getSupportModifier = (language: Language) => {
  if (isDefaultLanguage(language)) return '';
  return `${language}.`;
};

const isJoinSiteSupported = (region: Region, language: Language) => {
  const supportedSites: Record<string, Region[]> = {
    'de': [ Region.EU ],
    'fr': [ Region.CA, Region.EU]
  };
  if (isDefaultLanguage(language)) return true;
  return supportedSites[language]?.includes(region);
};

export const getJoinLocalizedUrl = (region: Region = DEFAULT_REGION, language: Language = DEFAULT_LANGUAGE, isProd: boolean = true) => {
  const joinSite = isJoinSiteSupported(region, language)
    ? `${language}-${region.toLowerCase()}`
    : `${DEFAULT_LANGUAGE}-${region.toLowerCase()}`;
  return `https://join${isProd ? '' : '.qa'}.whoop.com/${joinSite}/`
};

export const getPrivacyPolicyLocalizedUrl = (language: Language = DEFAULT_LANGUAGE) => {
  return `${getWhoopLocalizedUrl(language)}privacy/`;
};

export const getReturnPolicyLocalizedUrl = (language: Language = DEFAULT_LANGUAGE) => {
  return `${getSupportLocalizedUrl(language)}ACCOUNT__ORDERS/Refund%2C_Return%2C_Exchange/Return_Policy`;
};

export const getShopLocalizedUrl = (region: Region = DEFAULT_REGION, language: Language = DEFAULT_LANGUAGE, isProd: boolean = true) => {
  const shopRegion = getShopRegion(region);
  const shopSite = isJoinSiteSupported(shopRegion, language)
  ? `${language}-${shopRegion.toLowerCase()}`
  : `${DEFAULT_LANGUAGE}-${shopRegion.toLowerCase()}`;
  return `https://shop${isProd ? '' : '.qa'}.whoop.com/${shopSite}/`;
};

export const getSupportLocalizedUrl = (language: Language = DEFAULT_LANGUAGE) => {
  return `https://${getSupportModifier(language)}support.whoop.com/`;
};

export const getTermsOfSaleLocalizedUrl = (region: Region = DEFAULT_REGION, language: Language = DEFAULT_LANGUAGE) => {
  const whoopUrl = getWhoopLocalizedUrl(language);
  const regionModifier = getRegionModifier(region, language);
  return `${whoopUrl}termsofsale${regionModifier}/`;
};

export const getTermsOfUseLocalizedUrl = (region: Region = DEFAULT_REGION, language: Language = DEFAULT_LANGUAGE) => {
  const whoopUrl = getWhoopLocalizedUrl(language);
  const regionModifier = getRegionModifier(region, language);
  return `${whoopUrl}termsofuse${regionModifier}/`
};

export const getWebAppLocalizedUrl = (language: Language = DEFAULT_LANGUAGE, isProd: boolean = true) => {
  return `https://app${isProd ? '' : '.qa'}.whoop.com${getLanguageModifier(language)}/`;
}

export const getWhoopLocalizedUrl = (language: Language = DEFAULT_LANGUAGE) => {
  return `${BASE_WHOOP_URL}${getLanguageModifier(language)}/`;
};